<template>
  <div class="charts-section">
    <div v-if="suspendCharts" class="charts-overlay">
      <div class="spinner small" aria-hidden="true"></div>
      <p>{{ $t('charts.updatingCharts') }}</p>
    </div>

    <div class="charts-container">
      <template v-if="showZeroSinceStartNotice">
        <div class="chart-card empty-card">
          <div class="empty-notice">
            <h3>{{ $t('charts.noDataYet') }}</h3>
            <template v-if="newlyRegistered">
              <i18n-t scope="global" keypath="charts.alreadyCollected" tag="p" class="empty-paragraph">
                <template #hours><strong>{{ totalHours }}</strong></template>
                <template #games><strong>{{ totalGames }}</strong></template>
              </i18n-t>
              <p>{{ $t('charts.playAndComeBack') }}</p>
              <div class="top-games-preview-container" aria-live="polite">
                <div v-if="topGamesPreviewState.loading" class="preview-status">
                  <div class="spinner small" aria-hidden="true"></div>
                  <p>{{ $t('charts.loadingPreview') }}</p>
                </div>
                <p v-else-if="topGamesPreviewState.error" class="preview-status">{{ topGamesPreviewState.error }}</p>
                <template v-else-if="topGamesPreviewState.games.length">
                  <p class="preview-subheading">
                    {{ $t('charts.mostPlayedOnSteam') }}
                    <span v-if="topGamesPreviewState.totalGames > topGamesPreviewState.games.length">
                      {{ $t('charts.showingOf', { shown: topGamesPreviewState.games.length, total: topGamesPreviewState.totalGames }) }}
                    </span>
                    :
                  </p>
                  <ul class="top-games-preview">
                    <li
                      v-for="game in topGamesPreviewState.games"
                      :key="game.app_id"
                      class="top-games-item"
                    >
                      <div class="top-games-left">
                        <img
                          class="top-games-thumb"
                          :src="getGameImageUrl(game.app_id)"
                          :alt="resolvePreviewGameName(game)"
                          @error="$event.target.style.display='none'"
                          loading="lazy"
                        >
                        <span class="game-name">{{ resolvePreviewGameName(game) }}</span>
                      </div>
                      <span class="game-playtime">{{ formatPlaytime(game.minutes) }}</span>
                    </li>
                  </ul>
                  <div
                    v-if="topGamesPreviewState.achievements.total > 0"
                    class="preview-achievements"
                  >
                    <i18n-t scope="global" keypath="charts.achievementsUnlockedCount" tag="p" class="preview-subheading">
                      <template #count>🏆 <strong>{{ topGamesPreviewState.achievements.total.toLocaleString() }}</strong></template>
                    </i18n-t>
                    <div v-if="topGamesPreviewState.achievements.icons.length" class="preview-ach-icons">
                      <img
                        v-for="(ic, i) in topGamesPreviewState.achievements.icons"
                        :key="i"
                        class="preview-ach-icon"
                        :src="achievementIconUrl(ic.icon)"
                        :alt="ic.name || 'Achievement'"
                        :title="ic.name || 'Achievement'"
                        loading="lazy"
                        @error="$event.target.style.display='none'"
                      >
                    </div>
                  </div>
                  <p class="preview-footnote">{{ $t('charts.librarySnapshot') }}</p>
                </template>
                <p v-else class="preview-status">{{ $t('charts.couldntLoadGames') }}</p>
              </div>
              <!-- The one moment the product openly cannot deliver yet: day one
                   has no increments to show, so "come back tomorrow" is the
                   honest answer. That is exactly where a reason to come back
                   belongs. -->
              <ClaimCard :profile-steam-id="profileSteamId" variant="full" />
            </template>
            <template v-else>
              <p>{{ $t('charts.noDataPeriod') }}</p>
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <template v-if="rangeEmpty">
          <div class="chart-card empty-card">
            <div class="empty-notice">
              <h3>{{ $t('charts.noPlaytimeInRange') }}</h3>
              <p>{{ $t('charts.tryBroaderFilter') }}</p>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="chart-card">
            <div
              class="chart-card-controls"
              v-if="hasStackedChartData"
            >
              <label class="chart-toggle">
                <input
                  type="checkbox"
                  v-model="showLowPlaytimeGames"
                  :disabled="lowPlaytimeGamesCount === 0"
                  :aria-label="$t('charts.includeShortPlaytimes')"
                >
                <span>{{ $t('charts.includeShortPlaytimes') }}</span>
              </label>
            </div>
            <PlaytimeChart
              :data="displayStackedChartData"
              title=""
              type="bar"
              :stack-by-game="true"
              :date-range-start="normalizedRange.start"
              :date-range-end="normalizedRange.end"
              color="#f39c12"
              :suspend="suspendCharts"
              :key="'bar-all'"
              :format-date-label="formatDateLabel"
              :overlay-line-data="chartData"
              :overlay-line-label="$t('charts.totalPlaytimeTrend')"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import ClaimCard from './ClaimCard.vue'
import PlaytimeChart from '../PlaytimeChart.vue'
import { achievementIconUrl } from '../../utils/achievements'

export default {
  name: 'DashboardChartsSection',
  components: {
    ClaimCard,
    PlaytimeChart
  },
  data() {
    return {
      showLowPlaytimeGames: false
    }
  },
  props: {
    suspendCharts: {
      type: Boolean,
      default: false
    },
    showZeroSinceStartNotice: {
      type: Boolean,
      default: false
    },
    newlyRegistered: {
      type: Boolean,
      default: false
    },
    userProfile: {
      type: Object,
      default: () => ({})
    },
    // Needed by ClaimCard, which only renders for the profile's own owner.
    profileSteamId: {
      type: String,
      default: ''
    },
    topGamesPreview: {
      type: Object,
      default: () => ({})
    },
    chartData: {
      type: Array,
      default: () => []
    },
    stackedChartData: {
      type: Array,
      default: () => []
    },
    activeDateRange: {
      type: Object,
      default: () => ({ start: null, end: null })
    },
    formatObj: {
      type: Function,
      required: true
    },
    formatPlaytime: {
      type: Function,
      required: true
    },
    getGameImageUrl: {
      type: Function,
      required: true
    },
    resolvePreviewGameName: {
      type: Function,
      required: true
    },
    rangeEmpty: {
      type: Boolean,
      default: false
    },
    formatDateLabel: {
      type: Function,
      default: null
    }
  },
  computed: {
    normalizedRange() {
      const start = this.activeDateRange?.start
        ? this.safeFormat(this.activeDateRange.start)
        : ''
      const end = this.activeDateRange?.end
        ? this.safeFormat(this.activeDateRange.end)
        : ''
      return { start, end }
    },
    topGamesPreviewState() {
      const base = this.topGamesPreview || {}
      const ach = base.achievements && typeof base.achievements === 'object' ? base.achievements : { total: 0, icons: [] }
      return {
        loading: !!base.loading,
        error: base.error || '',
        games: Array.isArray(base.games) ? base.games : [],
        totalGames: typeof base.totalGames === 'number' ? base.totalGames : 0,
        achievements: {
          total: Number(ach.total) || 0,
          icons: Array.isArray(ach.icons) ? ach.icons : []
        }
      }
    },
    totalHours() {
      const hours = Number(this.userProfile?.totalHoursWasted) || 0
      return hours
    },
    totalGames() {
      const games = Number(this.userProfile?.totalNumberOfGames) || 0
      return games
    },
    hasStackedChartData() {
      return Array.isArray(this.stackedChartData) && this.stackedChartData.length > 0
    },
    lowPlaytimeThresholdMinutes() {
      return 5
    },
    lowPlaytimeAppIds() {
      const ids = new Set()
      if (!Array.isArray(this.stackedChartData)) return ids

      const threshold = this.lowPlaytimeThresholdMinutes
      for (const entry of this.stackedChartData) {
        const appId = entry?.app_id
        if (!appId) continue
        const minutes = Number(entry?.playtime_forever)
        if (!Number.isFinite(minutes)) continue
        if (minutes < threshold) ids.add(appId)
      }
      return ids
    },
    lowPlaytimeGamesCount() {
      return this.lowPlaytimeAppIds.size
    },
    displayStackedChartData() {
      if (!Array.isArray(this.stackedChartData)) return []
      if (this.showLowPlaytimeGames) return this.stackedChartData
      if (this.lowPlaytimeAppIds.size === 0) return this.stackedChartData

      const threshold = this.lowPlaytimeThresholdMinutes
      return this.stackedChartData.filter(entry => {
        const minutes = Number(entry?.playtime_forever)
        if (!Number.isFinite(minutes)) return false
        return minutes >= threshold
      })
    }
  },
  methods: {
    achievementIconUrl,
    safeFormat(value) {
      if (typeof value === 'string') {
        return value
      }
      try {
        return this.formatObj(value, 'yyyy-MM-dd')
      } catch (error) {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.charts-section {
  position: relative;
}

.charts-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 2;
  border-radius: 8px;
}

.charts-container {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  width: 100%;
}

.chart-card {
  flex: 1;
  min-width: 320px;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 12px;
  position: relative;
}

.chart-card-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.chart-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--muted);
}

.chart-toggle input {
  margin: 0;
}

.chart-toggle__hint {
  font-size: 0.85rem;
  color: var(--muted);
}

.empty-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.empty-notice {
  text-align: center;
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-notice h3 {
  margin: 0;
  font-size: 1.4rem;
}

.empty-paragraph {
  margin: 0;
  line-height: 1.45;
}

.top-games-preview-container {
  margin-top: 16px;
  background: rgba(8, 12, 20, 0.75);
  border: 1px solid rgba(102, 192, 244, 0.2);
  border-radius: 10px;
  padding: 16px;
}

.preview-status {
  margin: 0;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.preview-subheading {
  margin: 0 0 12px 0;
  font-weight: 600;
  color: var(--text);
}

.top-games-preview {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.top-games-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(8, 12, 20, 0.65);
}

.top-games-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.top-games-thumb {
  width: 80px;
  height: 37px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.game-name {
  font-weight: 600;
  color: var(--text);
}

.game-playtime {
  color: var(--muted);
  font-size: 0.9rem;
  flex-shrink: 0;
}

.preview-footnote {
  margin: 0;
  font-size: 0.85rem;
  color: var(--muted);
  text-align: center;
}

.preview-achievements {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--panel-border);
}

.preview-ach-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  justify-content: center;
}

.preview-ach-icon {
  width: 34px;
  height: 34px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--panel-border);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 24px;
  height: 24px;
  border-width: 3px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
